<template>
  <q-dialog :model-value="modelValue" persistent @update:model-value="$emit('update:modelValue', $event)">
    <q-card style="min-width: 500px">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">
          <q-icon name="save_as" class="q-mr-sm" />
          Save as Template
        </div>
        <q-space />
        <q-btn icon="close" flat round dense @click="$emit('update:modelValue', false)" />
      </q-card-section>

      <q-separator class="q-mt-md" />

      <q-card-section>
        <div class="q-gutter-md">
          <q-input
            :model-value="formData.name"
            label="Template Name *"
            filled
            :rules="[val => !!val || 'Template name is required']"
            hint="Choose a descriptive name for this template"
            @update:model-value="$emit('update:name', $event)"
          />

          <q-input
            :model-value="formData.description"
            label="Description"
            filled
            type="textarea"
            rows="3"
            hint="Describe what this template is for and when to use it"
            @update:model-value="$emit('update:description', $event)"
          />

          <!-- Options for Equipment Templates -->
          <div v-if="formData.type === 'equipment'" class="q-mt-md">
            <div class="text-subtitle2 q-mb-sm">Include in Template:</div>
            <q-checkbox
              :model-value="formData.includeDevices"
              label="Include all devices"
              color="primary"
              @update:model-value="$emit('update:include-devices', $event)"
            />
            <q-checkbox
              :model-value="formData.includePoints"
              label="Include all points"
              color="primary"
              class="q-ml-md"
              @update:model-value="$emit('update:include-points', $event)"
            />
          </div>

          <!-- Options for Zone Templates -->
          <div v-if="formData.type === 'zone'" class="q-mt-md">
            <div class="text-subtitle2 q-mb-sm">Include in Template:</div>
            <q-checkbox
              :model-value="formData.includeDevices"
              label="Include all devices"
              color="primary"
              @update:model-value="$emit('update:include-devices', $event)"
            />
            <q-checkbox
              :model-value="formData.includePoints"
              label="Include all points"
              color="primary"
              class="q-ml-md"
              @update:model-value="$emit('update:include-points', $event)"
            />
          </div>

          <!-- Options for Device Templates -->
          <div v-if="formData.type === 'device'" class="q-mt-md">
            <div class="text-subtitle2 q-mb-sm">Include in Template:</div>
            <q-checkbox
              :model-value="formData.includePoints"
              label="Include all points"
              color="primary"
              @update:model-value="$emit('update:include-points', $event)"
            />
          </div>

          <!-- Scope Selection -->
          <q-card flat bordered class="q-mt-md q-pa-sm">
            <div class="text-subtitle2 q-mb-sm">Template Scope:</div>
            <q-radio
              :model-value="formData.scope"
              val="site"
              label="Site-specific (only for this site)"
              color="primary"
              @update:model-value="$emit('update:scope', $event)"
            />
            <br>
            <q-radio
              :model-value="formData.scope"
              val="global"
              label="Global (available for all sites)"
              color="primary"
              @update:model-value="$emit('update:scope', $event)"
            />
          </q-card>

          <!-- Preview Section -->
          <q-card v-if="formData.itemData" flat bordered class="q-mt-md q-pa-md">
            <div class="text-subtitle2 q-mb-sm">Template Preview:</div>
            <div class="text-caption text-grey-7">
              <div><strong>Type:</strong> {{ formData.type }}</div>
              <div><strong>BRICK Class:</strong> {{ formData.itemData.brick_class || formData.itemData.brickClass }}</div>
              <div v-if="formData.type === 'equipment' && formData.deviceCount > 0">
                <strong>Devices:</strong> {{ formData.deviceCount }} device(s)
              </div>
              <div v-if="formData.pointCount > 0">
                <strong>Points:</strong> {{ formData.pointCount }} point(s)
              </div>
            </div>
          </q-card>
        </div>
      </q-card-section>

      <q-card-actions align="right" class="q-pa-md">
        <q-btn 
          flat 
          label="Cancel" 
          color="grey-7"
          @click="$emit('update:modelValue', false)"
        />
        <q-btn 
          unelevated
          label="Save as Template" 
          color="primary"
          icon="save"
          :loading="saving"
          :disable="!formData.name || saving"
          @click="$emit('submit')"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { defineProps, defineEmits } from 'vue';

  const props = defineProps({
    modelValue: { type: Boolean, required: true },
    formData: { type: Object, required: true },
    saving: { type: Boolean, default: false },
  });

  const emit = defineEmits([
    'update:modelValue',
    'update:name',
    'update:description',
    'update:include-devices',
    'update:include-points',
    'update:scope',
    'submit',
  ]);
</script>
